
import Vue from 'vue';
import { VueGoodTable } from 'vue-good-table';
import 'vue-good-table/dist/vue-good-table.css';
import { Component, Prop } from 'vue-property-decorator';
import { MeasurementSpecification, MeasurementSpecificationTemplate, MeasurementValueType } from '../../backend/backendapi';
import { paginationOptions, searchOptions } from '../../../util/vue-good-table';
import TestEquipmentMeasurementSpecificationEditModal from './TestEquipmentMeasurementSpecificationEditModal';
import { Like } from '../../../util/Like';
import { camelCase } from '../../../util';
import { getValue } from '../util';
import MeasurementSpecificationsTemplateBadges from '../measurementSpecificationTemplates/MeasurementSpecificationsTemplateBadges';

export type MeasurementSpecificationContainer = {
  measurementSpecifications?: MeasurementSpecification[] | undefined;
}

@Component({
  components: {
    MeasurementSpecificationsTemplateBadges,
    MeasurementSpecificationTemplateSelectionModal: //circular dependency, needs to loaded this way (dynamically)
        () => import('../measurementSpecificationTemplates/MeasurementSpecificationTemplateSelectionModal.vue'),
    TestEquipmentMeasurementSpecificationEditModal,
    VueGoodTable,
  },
})
export default class TestEquipmentMeasurementSpecifications extends Vue {
  @Prop({ required: true })
  readonly container!: MeasurementSpecificationContainer;
  @Prop({ required: true })
  readonly editable!: boolean;
  @Prop({ default: true })
  readonly showTemplateNames!: boolean;
  @Prop({ default: true })
  readonly inheritMedia!: boolean;

  get columns(): unknown[] {
    let ret: unknown[] = [
      {
        label: this.$t('testEquipment.measurementSpecification.model.name').toString(),
        field: (row: MeasurementSpecification) => row.name,
      },
      {
        label: this.$t('testEquipment.measurementSpecification.model.description').toString(),
        field: (row: MeasurementSpecification) => row.description,
      },
      {
        label: this.$t('testEquipment.measurementSpecification.model.type').toString(),
        field: (row: MeasurementSpecification) => row.type,
        formatFn: (type: MeasurementValueType) => this.$t('testEquipment.measurementValueType.' + camelCase(MeasurementValueType[type])).toString(),
      },
      {
        label: this.$t('testEquipment.measurementSpecification.model.lowerBound').toString(),
        field: (row: MeasurementSpecification) => row.type === MeasurementValueType.Float ? getValue(row.lowerBound, row.type) : null,
      },
      {
        label: this.$t('testEquipment.measurementSpecification.model.nominalValue').toString(),
        field: (row: MeasurementSpecification) => getValue(row.nominalValue, row.type),
      },
      {
        label: this.$t('testEquipment.measurementSpecification.model.upperBound').toString(),
        field: (row: MeasurementSpecification) => row.type === MeasurementValueType.Float ? getValue(row.upperBound, row.type) : null,
      },
      {
        label: this.$t('testEquipment.measurementSpecification.model.required').toString(),
        field: (row: MeasurementSpecification) => !row.optional,
        type: 'boolean',
      },
    ];
    if (this.editable) {
      ret.push({
        label: this.$t('general.actions').toString(),
        field: 'actions',
      });
    }
    return ret;
  }

  get paginationOptions(): any {
    return paginationOptions(this.$i18n);
  }

  get rows(): MeasurementSpecification[] {
    return this.container.measurementSpecifications ?? [];
  }

  get searchOptions(): any {
    return searchOptions(this.$i18n);
  }

  onClick(arg: { row: Like<MeasurementSpecification>, pageIndex: number, selected: boolean, event: MouseEvent }): void {
    if (!this.editable) {
      return;
    }

    this.onEdit(arg.row);
  }

  onDelete(specification: Like<MeasurementSpecification>): void {
    if (!this.container.measurementSpecifications) {
      return;
    }

    const index = this.container.measurementSpecifications.findIndex(e => e.id === specification.id);
    if (index >= 0) {
      this.container.measurementSpecifications.splice(index, 1);
    }
  }

  onEdit(specification: Like<MeasurementSpecification>): void {
    if (!this.container.measurementSpecifications) {
      return;
    }
    const original = this.container.measurementSpecifications.find(e => e.id === specification.id);
    (this.$refs.editModal as TestEquipmentMeasurementSpecificationEditModal).open(original);
  }

  onNew(): void {
    this.container.measurementSpecifications ??= [];

    //new unique id
    const usedIds = new Set(this.container.measurementSpecifications.map(e => e.id));
    let id: string;
    do {
      id = crypto.randomUUID();
    }
    while (usedIds.has(id));

    //
    const specification = MeasurementSpecification.fromJS(
        { id: id, type: MeasurementValueType.Float, lowerBound: '0', nominalValue: '0', upperBound: '0' });
    this.container.measurementSpecifications.push(specification);
    (this.$refs.editModal as TestEquipmentMeasurementSpecificationEditModal).open(specification);
  }

  onTemplateChoose(): void {
    // needs to be done this way because circular dependency
    // readable code: 
    // (this.$refs.selectModal as MeasurementSpecificationTemplateSelectionModal).open();
    (this.$refs.selectModal as Vue & { open: () => void }).open();
  }

  onTemplateChosen(template: MeasurementSpecificationTemplate): void {
    const mediaIds = (template.medias ?? []).map(e => e.mediaId);
    const specifications = (template.measurementSpecifications ?? []).map(e => {
      const ret = MeasurementSpecification.fromJS(e);
      ret.id = crypto.randomUUID();
      ret.templateId = template.id;
      ret.templateName = template.name;
      if (this.inheritMedia && mediaIds.length) {
        ret.mediaIds = mediaIds;
      }
      return ret;
    });

    this.container.measurementSpecifications ??= [];
    this.container.measurementSpecifications.push(...specifications);

    // needs to be done this way because circular dependency
    // readable code: 
    // (this.$refs.selectModal as MeasurementSpecificationTemplateSelectionModal).dismiss();
    (this.$refs.selectModal as Vue & { dismiss: () => void }).dismiss();
  }

  saveSpecification(specification: MeasurementSpecification): void {
    if (!this.container.measurementSpecifications) {
      return;
    }

    const index = this.container.measurementSpecifications.findIndex(e => e.id === specification.id);
    if (index < 0) {
      return;
    }

    this.container.measurementSpecifications.splice(index, 1, specification);
    (this.$refs.editModal as TestEquipmentMeasurementSpecificationEditModal).dismiss();
  }
}
